import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CAT_MAP } from "@/lib/categories";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { ChevronLeft, Lightbulb, Check } from "lucide-react";
import { Seo, breadcrumbLd, SITE_URL, SITE_NAME, clip } from "@/components/Seo";

export default function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setGuide(null); setNotFound(false);
    api.get(`/guides/${slug}`).then((res) => setGuide(res.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center font-editorial text-2xl text-stone-800">
      <Seo title="Guida non trovata" noindex />
      Guida non trovata
    </div>
  );
  if (!guide) return <div className="max-w-3xl mx-auto px-4 py-24 animate-pulse"><div className="h-14 rounded bg-panel mb-6" /><div className="h-80 rounded-xl bg-panel" /></div>;

  const cat = CAT_MAP[guide.category];
  const path = `/guida/${guide.slug}`;
  // Le vecchie guide AI si sovrappongono alle «Come iniziare»: canonical sulla pagina principale.
  const canonical = guide.canonical_path;

  return (
    <article className="max-w-3xl mx-auto px-4 py-10 fade-up">
      <Seo
        title={guide.title}
        description={clip(guide.intro)}
        path={canonical || path}
        image={guide.image_url}
        imageAlt={guide.title}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org", "@type": "Article",
            headline: guide.title?.slice(0, 110), description: guide.intro,
            image: guide.image_url ? [guide.image_url] : undefined,
            inLanguage: "it-IT",
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
            author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          },
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Guide", path: "/guide" },
            { name: guide.title, path },
          ]),
        ]}
      />
      <Link to={`/categoria/${guide.category}`} className="inline-flex items-center gap-1 text-sm font-semibold text-stone-500 hover:text-gold transition-colors mb-6">
        <ChevronLeft size={16} /> {cat?.name}
      </Link>

      {canonical && (
        <div data-testid="guide-canonical-notice"
          className="mb-6 rounded-2xl bg-panel border-2 border-line border-l-[6px] border-l-gold p-4 text-sm text-stone-700">
          Versione aggiornata e più completa:{" "}
          <Link to={canonical} data-testid="guide-canonical-link"
            className="font-semibold underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">
            Come iniziare a collezionare {cat?.name?.toLowerCase()}
          </Link>
        </div>
      )}

      <span className="font-meta text-[10px] uppercase tracking-[0.24em]" style={{ color: cat?.hex }}>Guida · {cat?.name}</span>
      <h1 className="font-editorial font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-stone-900 mt-3">
        {guide.title}
      </h1>

      <div className="aspect-[16/9] overflow-hidden rounded-3xl bg-panel border-2 border-line my-8">
        <img src={guide.image_url} alt={guide.title} className="w-full h-full object-cover" />
      </div>

      <p className="text-lg sm:text-xl text-stone-700 leading-relaxed font-light mb-10">{guide.intro}</p>

      <div className="space-y-9">
        {(guide.sections || []).map((s, i) => (
          <section key={i} data-testid={`guide-section-${i}`}>
            <h2 className={`font-editorial font-bold text-2xl text-stone-900 border-l-[6px] ${cat?.accent} pl-4 mb-3`}>{s.heading}</h2>
            <p className="text-[17px] text-stone-600 leading-[1.85]">{s.content}</p>
          </section>
        ))}
      </div>

      {guide.practical_tips?.length > 0 && (
        <div data-testid="guide-practical-tips" className={`mt-12 rounded-2xl bg-panel border-2 border-line border-l-[6px] ${cat?.accent} p-6`}>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={18} style={{ color: cat?.hex }} />
            <h2 className="font-editorial font-bold text-xl text-stone-900">In pratica: cosa fare</h2>
          </div>
          <ul className="space-y-2.5">
            {guide.practical_tips.map((t, i) => (
              <li key={i} className="flex gap-2.5 text-stone-700">
                <Check size={18} className="mt-0.5 shrink-0" style={{ color: cat?.hex }} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-12">
        <DisclaimerBanner />
      </div>
    </article>
  );
}
